import { AI_ASSETS, AI_ASSET_IDS, type AIAssetCategory, type AIAssetId, type AICollisionDefinition } from "@endclock/shared";
import { loadStaticGlb } from "../assets/gltf.js";
import { type GpuMesh, type Renderer, type StaticRenderMaterial } from "../engine/renderer.js";
import { ReusableInstanceBatch } from "../world/static-instances.js";

interface AIManifestEntry {
  readonly id: AIAssetId;
  readonly url: string;
  readonly category: AIAssetCategory;
  readonly triangleCount: number;
  readonly primitiveCount: number;
  readonly activePrimitiveCount: number;
  readonly collision: AICollisionDefinition;
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
}

interface AIManifest {
  readonly version: number;
  readonly format: string;
  readonly assets: readonly AIManifestEntry[];
}

export interface LoadedAIPrimitive {
  readonly mesh: GpuMesh;
  readonly baseColor: readonly [number, number, number, number];
  readonly activeMaterial: StaticRenderMaterial;
  readonly inactiveMaterial: StaticRenderMaterial;
  readonly activeBatch: ReusableInstanceBatch;
  readonly inactiveBatch: ReusableInstanceBatch;
}

export interface LoadedAIAsset {
  readonly primitives: readonly LoadedAIPrimitive[];
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
  readonly triangleCount: number;
}

export type AILoadState = "loading" | "ready" | "failed";

/** One manifest/GLB parse/GPU allocation per game context, shared by static and dynamic AI. */
export class AIAssetLibrary {
  state: AILoadState = "loading";
  loadError: string | undefined;
  readonly assets = new Map<AIAssetId, LoadedAIAsset>();
  readonly ready: Promise<void>;
  private staticObjects = 0;
  private staticTriangles = 0;
  private dynamicObjects = 0;
  private dynamicTriangles = 0;
  setStaticTotals(objects: number, triangles: number): void {
    this.staticObjects = objects; this.staticTriangles = triangles; this.updateTotals();
  }
  setDynamicTotals(objects: number, triangles: number): void {
    if (objects === this.dynamicObjects && triangles === this.dynamicTriangles) return;
    this.dynamicObjects = objects; this.dynamicTriangles = triangles; this.updateTotals();
  }
  private updateTotals(): void {
    this.renderer.metrics.totalAiObjects = this.staticObjects + this.dynamicObjects;
    this.renderer.metrics.totalAiTriangles = this.staticTriangles + this.dynamicTriangles;
  }
  constructor(private readonly renderer: Renderer) { this.ready = this.load(); }
  private async load(): Promise<void> {
    try {
      const response = await fetch("/assets/ai/manifest.json");
      if (!response.ok) throw new Error("AI asset manifest request failed");
      const manifest = (await response.json()) as AIManifest;
      if (
        manifest.version !== 1 ||
        manifest.format !== "glTF 2.0 binary" ||
        manifest.assets.length !== AI_ASSET_IDS.length ||
        new Set(manifest.assets.map((entry) => entry.id)).size !== AI_ASSET_IDS.length
      ) {
        throw new Error("Invalid AI asset manifest");
      }

      const loaded = await Promise.all(
        manifest.assets.map(async (entry) => {
          const definition = AI_ASSETS[entry.id];
          if (
            !definition ||
            definition.category !== entry.category ||
            !sameCollision(definition.collision, entry.collision) ||
            Math.abs(definition.boundsCenterY - entry.boundsCenterY) > 0.0001 ||
            Math.abs(definition.boundsRadius - entry.boundsRadius) > 0.0001 ||
            entry.triangleCount <= 0 ||
            entry.triangleCount > 900 ||
            entry.primitiveCount < 2 ||
            entry.primitiveCount > 4 ||
            entry.activePrimitiveCount < 1 ||
            entry.activePrimitiveCount > 2 ||
            entry.activePrimitiveCount > entry.primitiveCount
          ) {
            throw new Error(`AI asset metadata mismatch: ${entry.id}`);
          }
          const asset = await loadStaticGlb(entry.url);
          const triangleCount = asset.primitives.reduce(
            (sum, primitive) => sum + primitive.geometry.indices.length / 3,
            0,
          );
          const activePrimitiveCount = asset.primitives.filter(
            (primitive) => primitive.material.emissiveStrength > 0,
          ).length;
          if (
            asset.id !== entry.id ||
            triangleCount !== entry.triangleCount ||
            asset.primitives.length !== entry.primitiveCount ||
            activePrimitiveCount !== entry.activePrimitiveCount ||
            !sameCollision(asset.collision, entry.collision) ||
            Math.abs(asset.boundsCenterY - entry.boundsCenterY) > 0.0001 ||
            Math.abs(asset.boundsRadius - entry.boundsRadius) > 0.0001
          ) {
            throw new Error(`AI GLB mismatch: ${entry.id}`);
          }
          return [entry.id, asset, triangleCount] as const;
        }),
      );

      // Validate the complete material set before allocating any GPU resources.
      for (const [id, asset] of loaded) {
        for (const primitive of asset.primitives) {
          if (primitive.material.alphaMode !== "OPAQUE" || primitive.material.baseColorTexture) {
            throw new Error(`AI material mismatch: ${id}`);
          }
        }
      }

      for (const [id, asset, triangleCount] of loaded) {
        const primitives: LoadedAIPrimitive[] = [];
        for (const primitive of asset.primitives) {
          const mesh = this.renderer.createMesh(primitive.geometry);
          this.renderer.metrics.aiBufferBytes += mesh.bufferBytes;
          const common = { alphaCutoff: 0, doubleSided: false } as const;
          primitives.push({
            mesh,
            baseColor: primitive.material.baseColor,
            activeMaterial: {
              ...common,
              emissiveColor: primitive.material.emissiveColor,
              emissiveStrength: primitive.material.emissiveStrength,
              pulseStrength: primitive.material.pulseStrength,
            },
            inactiveMaterial: common,
            activeBatch: new ReusableInstanceBatch(),
            inactiveBatch: new ReusableInstanceBatch(),
          });
        }
        this.assets.set(id, {
          primitives,
          boundsCenterY: asset.boundsCenterY,
          boundsRadius: asset.boundsRadius,
          triangleCount,
        });
      }

      this.state = "ready";
    } catch (error) {
      this.state = "failed";
      this.loadError = error instanceof Error ? error.message : "Unknown AI asset load error";
      console.error(this.loadError);
    }
  }
}

function sameCollision(left: AICollisionDefinition, right: AICollisionDefinition): boolean {
  if (left.shape !== right.shape) return false;
  if (left.shape === "none" && right.shape === "none") return true;
  if (left.shape === "circle" && right.shape === "circle") return close(left.radius, right.radius);
  if (left.shape === "box" && right.shape === "box") {
    return close(left.halfWidth, right.halfWidth) && close(left.halfDepth, right.halfDepth);
  }
  if (left.shape === "capsule" && right.shape === "capsule") {
    return close(left.radius, right.radius) && close(left.halfLength, right.halfLength);
  }
  return false;
}

function close(left: number, right: number): boolean {
  return Math.abs(left - right) <= 0.0001;
}
